// src/utils/helpers/colors.js (ESM)
// *******************************************************************************
// IMPORTS
// *******************************************************************************

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
let __hCursor = Math.random(); // seed inicial; pode ser fixo se quiser determinismo
const GOLDEN_RATIO_CONJUGATE = 0.618033988749895; // 1 - φ

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export const generateHexColor = (toHexOppositeColor = null, blackWhite = false, opts = {}) => {
  if (toHexOppositeColor) return invertColor(toHexOppositeColor, blackWhite);
  return vibrantHexGolden(opts);
};

export const opacityToHex = opacity => {
  // Ensure opacity is between 0 and 1
  const clampedOpacity = Math.max(0, Math.min(1, opacity));
  // Convert to hexadecimal (0 to 255 range)
  const alpha = Math.round(clampedOpacity * 255);
  // Convert to two-digit hex and pad with 0 if needed
  return alpha.toString(16).padStart(2, '0').toUpperCase();
};

// *******************************************************************************
// HELPERS
// *******************************************************************************

function vibrantHexGolden(opts) {
  // Opções ajustáveis (saturação/luminosidade)
  const s = clamp01(opts.s ?? 0.9); // alta saturação
  const l = clamp01(opts.l ?? 0.5); // luminosidade média

  // Permite resetar a sequência opcionalmente (determinismo entre execuções)
  if (typeof opts.seed === 'number') {
    __hCursor = frac(opts.seed);
  }

  // Avança a fração de matiz com o passo dourado e converte para 0..359
  __hCursor = frac(__hCursor + GOLDEN_RATIO_CONJUGATE);
  const h = Math.floor(__hCursor * 360);

  return hslToHex(h, s, l);
}

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));

  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];

  const m = l - c / 2;
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);

  return `#${pad2(r.toString(16))}${pad2(g.toString(16))}${pad2(b.toString(16))}`;
}

function invertColor(hex, blackWhite = false) {
  if (hex.indexOf('#') === 0) hex = hex.slice(1);

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length > 8) {
    console.error(`Invalid hex color: ${hex}!`);
    return `#${hex.slice(0, 6)}`;
  }

  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);

  if (blackWhite) {
    // luminance perceptual; threshold ~186 funciona bem para preto/branco
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#ffffff';
  }

  r = (255 - r).toString(16);
  g = (255 - g).toString(16);
  b = (255 - b).toString(16);

  return `#${pad2(r)}${pad2(g)}${pad2(b)}`;
}

function pad2(str) {
  return str.length === 1 ? `0${str}` : str;
}

function clamp01(x) {
  return Math.min(1, Math.max(0, x));
}

function frac(x) {
  return x - Math.floor(x);
}
